import { EYE_CLOSED_THRESHOLD, EYE_OPEN_THRESHOLD } from '../constants';
import type { FaceLandmark, FrameResult } from '../types';
import { BaseChallenge } from './base-challenge';

const LEFT_EYE_OUTER = 33;
const LEFT_EYE_INNER = 133;
const LEFT_EYE_UPPER = 159;
const LEFT_EYE_LOWER = 145;
const RIGHT_EYE_OUTER = 362;
const RIGHT_EYE_INNER = 263;
const RIGHT_EYE_UPPER = 386;
const RIGHT_EYE_LOWER = 374;
const BLINKS_REQUIRED = 2;

function eyeOpenness(
  landmarks: FaceLandmark[],
  outer: number,
  inner: number,
  upper: number,
  lower: number,
): number {
  const outerL = landmarks[outer];
  const innerL = landmarks[inner];
  const upperL = landmarks[upper];
  const lowerL = landmarks[lower];

  if (!outerL || !innerL || !upperL || !lowerL) return 1;

  const w = Math.abs(outerL.x - innerL.x);
  if (w < 0.001) return 1;

  const h = Math.abs(upperL.y - lowerL.y);
  return h / w;
}

export class BlinkChallenge extends BaseChallenge {
  private blinkCount = 0;

  private eyesClosed = false;

  constructor() {
    super('blink');
  }

  processFrame(frame: FrameResult): void {
    if (!frame.face?.landmarks || frame.face.landmarks.length < 400) {
      return;
    }

    this.captureFrame();

    const { landmarks } = frame.face;

    const leftEAR = eyeOpenness(
      landmarks,
      LEFT_EYE_OUTER,
      LEFT_EYE_INNER,
      LEFT_EYE_UPPER,
      LEFT_EYE_LOWER,
    );
    const rightEAR = eyeOpenness(
      landmarks,
      RIGHT_EYE_OUTER,
      RIGHT_EYE_INNER,
      RIGHT_EYE_UPPER,
      RIGHT_EYE_LOWER,
    );
    const ear = (leftEAR + rightEAR) / 2;

    if (!this.eyesClosed && ear < EYE_CLOSED_THRESHOLD) {
      this.eyesClosed = true;
    } else if (this.eyesClosed && ear > EYE_OPEN_THRESHOLD) {
      this.eyesClosed = false;
      this.blinkCount++;
    }

    const progress = Math.min(this.blinkCount / BLINKS_REQUIRED, 1);
    this.reportProgress(progress);

    if (this.blinkCount >= BLINKS_REQUIRED) {
      this.pass(0.95);
    }
  }
}
